import { action, computed, makeObservable, observable } from 'mobx'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { CursorPageQuery, Id } from 'types/definitions'
import {
  FiltersViewModel,
  FilterItemSource,
  FilterStorage,
  FilterItem,
} from 'infra/FiltersViewModel'
import {
  FetchUserWorkspaceMemberships,
  GetCurrentUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import WorkspacePosting, {
  WorkspacePostingStatus,
} from '../../WorkspacePosting'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { TableSortOrder } from 'components/Table/ClickableTable'
import { SearchableStaffingSortField } from './StaffingTableColumns'
import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import { WorkspacePostingsQuery } from '../../dtos/payloads'
import { task } from 'mobx-task'
import {
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs'
import { toStream, tapAction } from 'utils/mobx-util'
import { shareLatest, withCancellation } from 'utils/rx-util'
import { match } from 'utils/pattern-match-util'
import { CancellationToken } from 'infra/cancellation'
import { identity, omit, uniq } from 'lodash'
import { areDeepEqual } from 'utils/object-util'
import { WorkspacePostingStatusDTO } from '../../dtos/workspace-posting'

export interface WorkspacePostingSort {
  field: SearchableStaffingSortField
  direction: TableSortOrder
}

export type WorkspacePostingStatusFilterOption =
  | 'all'
  | 'draft'
  | 'active'
  | 'inReview'
  | 'closed'

export interface WorkspacePostingStatusFilter {
  count?: number
  type: WorkspacePostingStatusFilterOption
}

const WorkspacePostingStatusFilter = {
  none: {
    count: 0,
    type: 'all',
  } as WorkspacePostingStatusFilter,
}

export type WorkspacePostingFilter = {
  workspaceIds: Id[]
  status: WorkspacePostingStatusFilter
}

export type WorkspacePostingSearchFilter = {
  workspaceIds: Id[]
  statuses: WorkspacePostingStatus[]
  sort: WorkspacePostingSort
}

export const WorkspacePostingSearchFilter = {
  /**
   * Empty search filter.
   */
  none: {
    workspaceIds: [],
    sort: { field: 'workspace_id', direction: 'none' },
    statuses: [],
  } as WorkspacePostingSearchFilter,

  /**
   * Whether search filter is empty.
   */
  isNone: (searchFilter: WorkspacePostingSearchFilter) =>
    WorkspacePostingSearchFilter.equals(
      omit(WorkspacePostingSearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether two search filters are equal.
   */
  equals: (
    left: Partial<WorkspacePostingSearchFilter>,
    right: Partial<WorkspacePostingSearchFilter>,
  ) => areDeepEqual(left, right),
}

export interface WorkspacePostingSearchResult {
  searchFilter: WorkspacePostingSearchFilter
  filtersApplied: boolean
  readonly workspacePostings: readonly WorkspacePosting[]
}

/**
 * Checkbox selection states.
 */
export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',
  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',
  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

export default class StaffingSearchViewModel {
  /**
   * Observable subscription.
   */
  private subscription = new Subscription()

  filters = new FiltersViewModel<WorkspacePostingFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspaceIds: {
        title: 'Workspaces',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(this.workspacesWithStaffingPermission),
            true,
          ),
      },
      status: {
        icon: 'pending',
        title: 'Quick Select',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            WorkspacePostingStatusFilter.none,
            this.getStatusList.bind(this),
          ),
      },
    },
  })

  /**
   * Sorting.
   */
  sorting: WorkspacePostingSort = {
    field: 'workspace_id',
    direction: 'none',
  }

  /**
   * Search text.
   */
  searchText = ''

  /**
   * Staffing scroller.
   */
  scroller = new InfiniteScrollProvider<
    WorkspacePosting,
    WorkspacePostingSearchFilter
  >({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this._fetchWorkspacePostings(
        this.toFindWorkspacePostingQuery(paging, filter),
      ),
  })

  /**
   * Collection of selected workspace postings.
   */
  selected = new Set<WorkspacePosting>()

  /**
   * Tracks details for the previous search.
   */
  private _lastSearch: WorkspacePostingSearchResult = {
    searchFilter: WorkspacePostingSearchFilter.none,
    filtersApplied: false,
    workspacePostings: [],
  }

  /**
   * Search filter pipeline.
   */
  private searches$ = combineLatest([
    this.filters.appliedFilters$,
    toStream(() => this.sorting, true),
  ]).pipe(
    map(
      ([appliedFilters, sort]): WorkspacePostingSearchFilter => ({
        workspaceIds: appliedFilters.workspaceIds || [],
        statuses: toWorkspacePostingStatus(appliedFilters.status?.type),
        sort,
      }),
    ),
    distinctUntilChanged<WorkspacePostingSearchFilter>(
      WorkspacePostingSearchFilter.equals,
    ),
    switchMap(
      (query): Observable<WorkspacePostingSearchResult> =>
        withCancellation(async (ct) => this._searchItems(query, ct)),
    ),
    switchMap(async (result) => {
      await this.workspaceFilter.loadOptions()
      await this.statusFilter.loadOptions()
      return result
    }),
    tap(() => this.cleanSelected()),
    shareLatest(),
  )

  constructor(
    private workspacePostingStore: WorkspacePostingStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private filterStorage?: FilterStorage,
  ) {
    makeObservable(this, {
      sorting: observable,
      searchText: observable,
      selected: observable,
      workspacePostings: computed,
      workspaceFilter: computed,
      statusFilter: computed,
      filteredItems: computed,
      workspacesWithStaffingPermission: computed,
      selectionState: computed,
      togglePosting: action.bound,
      toggleAllPostings: action.bound,
      cleanSelected: action.bound,
      clearSelected: action.bound,
      setSortMethod: action.bound,
      setSortDirection: action.bound,
      setSortField: action.bound,
      setSearchText: action.bound,
      refreshStatusFilter: action.bound,
    })
  }

  /**
   * Activation of filters and subscription to search observable.
   */
  activate = task.resolved(async () => {
    this.filters.activate()
    this.subscription = this.searches$
      .pipe(tapAction((search) => (this._lastSearch = search)))
      .subscribe()
    await this.fetchWorkspaceMemberships()
  })

  /**
   * Deactivation of filters and unsubscription(?) to search observable.
   */
  deactivate() {
    this.searchText = ''
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }

  /**
   * Workspaces in which the user can manage staffing.
   * @returns
   */
  get workspacesWithStaffingPermission(): FilterItem<Id>[] {
    return this.getWorkspaceMemberships()
      .filter(
        (mb) =>
          mb.workspace.config.staffing_config?.enabled &&
          mb.member.hasSomePermissions('ADMIN_STAFFING_MANAGE'),
      )
      .map((mbs) => {
        return { title: mbs.workspace.name, value: mbs.workspace.id }
      })
  }

  /**
   * Returns whether posting is currently selected.
   * @param posting
   * @returns
   */
  isSelected(posting: WorkspacePosting): boolean {
    return this.selected.has(posting)
  }

  /**
   * Returns whether scroller items are all selected, none selected, or in between.
   */
  get selectionState(): SelectionState {
    if (this.selected.size === 0) {
      return SelectionState.NoneSelected
    }

    if (this.scroller.items.length === this.selected.size) {
      return SelectionState.AllSelected
    }

    if (this.selected.size > 0) {
      return SelectionState.SomeSelected
    }

    return SelectionState.NoneSelected
  }

  /**
   * Toggles whether an individual posting is selected.
   */
  togglePosting(posting: WorkspacePosting) {
    if (this.isSelected(posting)) {
      this.selected.delete(posting)
    } else {
      this.selected.add(posting)
    }
  }

  /**
   * Toggles whether all postings are selected.
   */
  toggleAllPostings() {
    switch (this.selectionState) {
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        this.scroller.items.forEach((posting) => {
          this.selected.add(posting)
        })
        break
      case SelectionState.AllSelected:
        this.clearSelected()
        break
    }
  }

  /**
   * Removes postings not present in current scroller items.
   */
  cleanSelected() {
    for (const posting of this.selected) {
      if (!this.scroller.items.includes(posting)) {
        this.selected.delete(posting)
      }
    }
  }

  /**
   * Removes all selected postings.
   */
  clearSelected() {
    this.selected.clear()
  }

  /**
   * Status list for status filter. Posting aggregations queried for number of postings per status filter option.
   * @returns
   */
  async getStatusList(): Promise<FilterItem<WorkspacePostingStatusFilter>[]> {
    const allowedWorkspaces = this.workspacesWithStaffingPermission.map((w) =>
      Id.assert(w.value),
    )
    const queryWorkspaces = this.workspaceFilter.value.map(Id.assert) || []
    await this.workspacePostingStore.fetchAggregations({
      workspace_ids:
        queryWorkspaces.length > 0 ? queryWorkspaces : allowedWorkspaces,
    })

    return [
      {
        title: 'All',
        value: {
          type: 'all',
          count: this.getWorkspacePostingsByStatusCount('all'),
        },
      },
      {
        title: 'Draft',
        value: {
          type: 'draft',
          count: this.getWorkspacePostingsByStatusCount('draft'),
        },
      },
      {
        title: 'Active',
        value: {
          type: 'active',
          count: this.getWorkspacePostingsByStatusCount('active'),
        },
      },
      {
        title: 'In review',
        value: {
          type: 'inReview',
          count: this.getWorkspacePostingsByStatusCount('inReview'),
        },
      },
      {
        title: 'Closed',
        value: {
          type: 'closed',
          count: this.getWorkspacePostingsByStatusCount('closed'),
        },
      },
    ]
  }

  /**
   * Sets the staffing sort.
   * @param sortMethod
   */
  setSortMethod(sortMethod: WorkspacePostingSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the staffing sort direction in particular.
   * @param direction
   */
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ field: 'workspace_id', direction: 'none' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Sets the staffing sort field in particular.
   * @param field
   */
  setSortField(field: SearchableStaffingSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Sets the search text.
   * @param value
   */
  setSearchText(value: string) {
    this.searchText = value
  }

  /**
   * Returns workspace postings, as currently filtered.
   */
  get workspacePostings() {
    return identity(uniq(this.scroller.items))
  }

  /**
   * Returns workspace filter.
   */
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  /**
   * Returns status filter.
   */
  get statusFilter() {
    return this.filters.getFilter('status') as SingleSelectFilter<
      string,
      WorkspacePostingStatusFilter
    >
  }

  // Is this how filtering by search text is supposed to work?
  get filteredItems() {
    if (!this.searchText) {
      return this.scroller.items
    }

    const text = this.searchText.toLowerCase()
    return this.scroller.items.filter(
      (wp) =>
        wp.workspace.name.toLocaleLowerCase().includes(text) ||
        wp.location.toLowerCase()?.includes(text) ||
        wp.primarySoftware.toLowerCase()?.includes(text) ||
        wp.supplementalSoftware.toLowerCase()?.includes(text),
    )
  }

  /**
   * Runs a search with a query, and returns the query and filters used, as well as the postings themselves.
   * @param query
   * @param ct
   * @returns
   */
  async _searchItems(
    query: WorkspacePostingSearchFilter,
    ct?: CancellationToken,
  ) {
    await this.scroller.fetch(query)

    while (this.workspacePostings.length === 0 && !this.scroller.isDone) {
      if (ct) {
        ct.throwIfCancelled()
      }

      await this.scroller.more()
    }

    return {
      searchFilter: query,
      filtersApplied: !WorkspacePostingSearchFilter.isNone(query),
      workspacePostings: this.workspacePostings,
    }
  }

  /**
   * Refresh status filter, I guess.
   */
  async refreshStatusFilter() {
    await this._searchItems(this._lastSearch.searchFilter)
    await this.statusFilter.loadOptions()
  }

  /**
   * Assembles filters and paging into the workspace posting query.
   * @param paging
   * @param filter
   * @returns
   */
  toFindWorkspacePostingQuery(
    paging: CursorPageQuery,
    filter?: WorkspacePostingSearchFilter,
  ): WorkspacePostingsQuery {
    const allowedWorkspaces = this.workspacesWithStaffingPermission.map((w) =>
      Id.assert(w.value),
    )
    const queryWorkspaces = filter?.workspaceIds.map(Id.assert) || []
    const filterStatuses = filter
      ? filter.statuses
          .map((s) => toWorkspacePostingStatusDTO(s))
          .filter((s): s is WorkspacePostingStatusDTO => s !== undefined)
      : []

    return {
      workspace_ids:
        queryWorkspaces.length > 0 ? queryWorkspaces : allowedWorkspaces,
      // If we are filtering by all five statuses, we may as well just query none.
      status: filterStatuses.length === 5 ? [] : filterStatuses,
      limit: paging.limit,
      after: paging.after,
    }
  }

  /**
   * Fetches a workspace posting list, given a particular query.
   * @param query
   * @returns
   */
  async _fetchWorkspacePostings(query: WorkspacePostingsQuery) {
    return await this.workspacePostingStore.fetchWorkspacePostings(query)
  }

  /**
   * Workspace postings filtered by status.
   * @param status
   * @returns
   */
  getWorkspacePostingsByStatusCount(
    status: WorkspacePostingStatusFilterOption,
  ) {
    return this.workspacePostingStore.workspacePostingAggregations
      .filter((postingsByStatus) => {
        return toWorkspacePostingStatus(status).includes(
          postingsByStatus?.status,
        )
      })
      .reduce((acc, next) => acc + next.total, 0)
  }
}

/**
 * Returns workspace posting statuses relevant for a status filter option.
 * @param status
 * @returns
 */
function toWorkspacePostingStatus(status?: WorkspacePostingStatusFilterOption) {
  if (!status) {
    return []
  }

  return match(status, {
    all: () => [
      WorkspacePostingStatus.Active,
      WorkspacePostingStatus.Cancelled,
      WorkspacePostingStatus.Closed,
      WorkspacePostingStatus.Draft,
      WorkspacePostingStatus.InReview,
    ],
    draft: () => [WorkspacePostingStatus.Draft],
    active: () => [WorkspacePostingStatus.Active],
    inReview: () => [WorkspacePostingStatus.InReview],
    closed: () => [
      WorkspacePostingStatus.Cancelled,
      WorkspacePostingStatus.Closed,
    ],
  })
}

/**
 * Returns the workspace posting status DTO for a given workspace posting status.
 * @param status
 * @returns
 */
export function toWorkspacePostingStatusDTO(
  status: WorkspacePostingStatus,
): WorkspacePostingStatusDTO | undefined {
  switch (status) {
    case WorkspacePostingStatus.Active:
      return WorkspacePostingStatusDTO.Active
    case WorkspacePostingStatus.Cancelled:
      return WorkspacePostingStatusDTO.Cancelled
    case WorkspacePostingStatus.Closed:
      return WorkspacePostingStatusDTO.Closed
    case WorkspacePostingStatus.Draft:
      return WorkspacePostingStatusDTO.Draft
    case WorkspacePostingStatus.InReview:
      return WorkspacePostingStatusDTO.InReview
    default:
      return undefined
  }
}
